import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  MenuItem,
} from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import {
  getGame,
  saveGameSettings,
  saveKnowledgeBaseItems,
  loadKbVideo,
  loadPoster,
  getProfile,
} from "../../redux/asyncThunk/userActions";
import { sanitizePlainText } from "../../utils/inputValidation";
import {
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../../utils/projectAccess";
import {
  collectTopicDescendantIds,
  createEmptyTopic,
  getRootTopics,
  getTopicChildren,
  getTopicPath,
  getTopicRootId,
  normalizeKbData,
  topicHasChildren,
} from "../../utils/kbTopics";
import ProjectTeamAccess from "../ProjectTeamAccess";
import ProjectDeleteSection from "../ProjectDeleteSection";
import { readAvailableMessengersFromSettings } from "../ProjectMessengerAvailability";
import { AUTH_FIELD_SX } from "../../pages/AuthLayout";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import "../trainer/trainer-editor.css";
import "./knowledge-base-editor.css";

const { addMessage } = userSlice;
const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

function createEmptyItem(topicId) {
  return {
    id: `kb-${Date.now()}`,
    topicId,
    question: "",
    answerMode: "text",
    answerText: "",
    hasVideo: false,
    screenpath: "",
  };
}

function isItemComplete(item) {
  if (!item?.question?.trim()) return false;
  if (item.answerMode === "video") return Boolean(item.hasVideo);
  return Boolean(item.answerText?.trim());
}

function isTopicComplete(topic) {
  return Boolean(topic?.title?.trim());
}

function formatQuestionCount(count) {
  const n = Number(count) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${n} вопросов`;
  if (mod10 === 1) return `${n} вопрос`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} вопроса`;
  return `${n} вопросов`;
}

function TopicTreeNodes({
  topics,
  roots,
  depth,
  activeTopicId,
  onSelect,
  onDelete,
}) {
  if (!roots.length) return null;

  return (
    <ul className={depth === 0 ? "kb-editor__topic-list" : "kb-editor__topic-children"}>
      {roots.map((topic) => (
        <li key={topic.id}>
          <div
            className={`kb-editor__topic-item${
              activeTopicId === topic.id ? " kb-editor__topic-item--active" : ""
            }`}
            style={{ paddingLeft: `${12 + depth * 14}px` }}
          >
            <button
              type="button"
              className="kb-editor__topic-btn"
              onClick={() => onSelect(topic.id)}
            >
              <FolderOutlinedIcon fontSize="small" />
              {topic.title?.trim() || "Без названия"}
            </button>
            <IconButton
              size="small"
              className="kb-editor__delete-btn"
              aria-label="Удалить тему"
              onClick={() => onDelete(topic.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </div>
          <TopicTreeNodes
            topics={topics}
            roots={getTopicChildren(topics, topic.id)}
            depth={depth + 1}
            activeTopicId={activeTopicId}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        </li>
      ))}
    </ul>
  );
}

export default function KnowledgeBaseEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopedRootId = searchParams.get("topic");
  const game = useSelector((state) => state.user.game) ?? initialGame;
  const user = useSelector((state) => state.user?.data?.user);
  const profile = useSelector((state) => state.user?.profile);

  const initialData = useMemo(() => normalizeKbData(game), [game]);

  const [title, setTitle] = useState(game?.title ?? "");
  const [description, setDescription] = useState(game?.description ?? "");
  const [topics, setTopics] = useState(initialData.topics);
  const [items, setItems] = useState(initialData.items);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const [activeItemId, setActiveItemId] = useState(null);
  const [settings, setSettings] = useState({
    projectStatus: getProjectStatusFromSettings(game?.settings),
    completion_text: game?.settings?.completion_text ?? "",
    author_telegram: game?.settings?.author_telegram ?? "",
    allow_replay: game?.settings?.allow_replay !== false,
  });
  const [view, setView] = useState("editor");
  const [availableMessengers, setAvailableMessengers] = useState(
    readAvailableMessengersFromSettings(game?.settings),
  );

  const contentSaveTimerRef = useRef(null);
  const settingsSaveTimerRef = useRef(null);
  const lastSavedContentRef = useRef("");
  const lastSavedSettingsRef = useRef("");
  const loadedGameIdRef = useRef(null);
  const topicsRef = useRef(topics);
  const itemsRef = useRef(items);

  topicsRef.current = topics;
  itemsRef.current = items;

  const activeTopic = useMemo(
    () => topics.find((topic) => topic.id === activeTopicId) ?? null,
    [topics, activeTopicId],
  );

  const activeItem = useMemo(
    () => items.find((item) => item.id === activeItemId) ?? null,
    [items, activeItemId],
  );

  const topicItems = useMemo(
    () => items.filter((item) => item.topicId === activeTopicId),
    [items, activeTopicId],
  );

  const activeTopicIsLeaf = activeTopic
    ? !topicHasChildren(topics, activeTopic.id)
    : false;

  const topicPath = useMemo(
    () => (activeTopicId ? getTopicPath(topics, activeTopicId) : []),
    [topics, activeTopicId],
  );

  const visibleTopicRoots = useMemo(() => {
    if (scopedRootId) {
      const scopedTopic = topics.find((topic) => topic.id === scopedRootId);
      return scopedTopic ? [scopedTopic] : [];
    }
    return getRootTopics(topics);
  }, [topics, scopedRootId]);

  const syncRootTopicParam = useCallback(
    (topicId) => {
      const rootId = getTopicRootId(topics, topicId);
      if (!rootId) return;

      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("type", "knowledge_base");
          next.set("topic", rootId);
          return next;
        },
        { replace: true },
      );
    },
    [topics, setSearchParams],
  );

  const handleSelectTopic = useCallback(
    (topicId) => {
      setActiveTopicId(topicId);
      setActiveItemId(null);

      const rootId = getTopicRootId(topics, topicId);
      if (rootId && rootId !== scopedRootId) {
        syncRootTopicParam(topicId);
      }
    },
    [topics, scopedRootId, syncRootTopicParam],
  );

  useEffect(() => {
    if (user?.id && !profile?.login) {
      dispatch(getProfile({ id: user.id }));
    }
  }, [dispatch, user?.id, profile?.login]);

  const buildSettingsSnapshot = useCallback(
    () =>
      JSON.stringify({
        title: title.trim(),
        description,
        settings,
        availableMessengers,
      }),
    [title, description, settings, availableMessengers],
  );

  const buildSavableContentSnapshot = useCallback(
    (topicsList, itemsList) =>
      JSON.stringify({
        topics: topicsList.filter(isTopicComplete),
        items: itemsList.filter(isItemComplete),
      }),
    [],
  );

  useEffect(() => {
    if (!game) return;
    setTitle(game.title ?? "");
    setDescription(game.description ?? "");
    setSettings({
      projectStatus: getProjectStatusFromSettings(game.settings),
      completion_text: game.settings?.completion_text ?? "",
      author_telegram: game.settings?.author_telegram ?? "",
      allow_replay: game.settings?.allow_replay !== false,
    });
    setAvailableMessengers(readAvailableMessengersFromSettings(game.settings));
    lastSavedSettingsRef.current = JSON.stringify({
      title: (game.title ?? "").trim(),
      description: game.description ?? "",
      settings: {
        projectStatus: getProjectStatusFromSettings(game.settings),
        completion_text: game.settings?.completion_text ?? "",
        author_telegram: game.settings?.author_telegram ?? "",
        allow_replay: game.settings?.allow_replay !== false,
      },
      availableMessengers: readAvailableMessengersFromSettings(game.settings),
    });
  }, [game]);

  useEffect(() => {
    if (!game?._id) return;

    const normalized = normalizeKbData(game);
    const serverSnapshot = buildSavableContentSnapshot(
      normalized.topics,
      normalized.items,
    );
    const isNewGame = loadedGameIdRef.current !== game._id;

    if (isNewGame) {
      loadedGameIdRef.current = game._id;
      clearTimeout(contentSaveTimerRef.current);
      setTopics(normalized.topics);
      setItems(normalized.items);
      lastSavedContentRef.current = serverSnapshot;
      return;
    }

    if (serverSnapshot === lastSavedContentRef.current) return;

    const localSnapshot = buildSavableContentSnapshot(topics, items);
    if (localSnapshot !== lastSavedContentRef.current) return;

    clearTimeout(contentSaveTimerRef.current);
    setTopics(normalized.topics);
    setItems(normalized.items);
    lastSavedContentRef.current = serverSnapshot;
  }, [game, topics, items, buildSavableContentSnapshot]);

  useEffect(() => {
    if (!game?._id || !topics.length) return;

    const roots = getRootTopics(topics);
    if (!roots.length) return;

    const validScopedRoot =
      scopedRootId && topics.some((topic) => topic.id === scopedRootId)
        ? scopedRootId
        : null;

    if (!validScopedRoot) {
      const fallbackRootId = roots[0].id;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("type", "knowledge_base");
          next.set("topic", fallbackRootId);
          return next;
        },
        { replace: true },
      );
      setActiveTopicId(fallbackRootId);
      setActiveItemId(null);
      return;
    }

    setActiveTopicId((current) => {
      if (current && topics.some((topic) => topic.id === current)) {
        const currentRootId = getTopicRootId(topics, current);
        if (currentRootId === validScopedRoot) return current;
      }
      return validScopedRoot;
    });
  }, [game?._id, scopedRootId, topics, setSearchParams]);

  const persistContent = useCallback(
    async (topicsToSave, itemsToSave) => {
      if (!game?._id) return;

      const currentSnapshot = buildSavableContentSnapshot(
        topicsRef.current,
        itemsRef.current,
      );
      const requestedSnapshot = buildSavableContentSnapshot(
        topicsToSave,
        itemsToSave,
      );
      if (requestedSnapshot !== currentSnapshot) return;

      const completeTopics = topicsToSave.filter(isTopicComplete);
      const completeItems = itemsToSave.filter(isItemComplete);
      try {
        await dispatch(
          saveKnowledgeBaseItems({
            gameId: game._id,
            topics: completeTopics,
            items: completeItems,
          }),
        ).unwrap();

        if (
          buildSavableContentSnapshot(topicsRef.current, itemsRef.current) !==
          requestedSnapshot
        ) {
          return;
        }

        lastSavedContentRef.current = buildSavableContentSnapshot(
          topicsToSave,
          itemsToSave,
        );
      } catch {
        dispatch(addMessage({ type: "error", message: "Не удалось сохранить" }));
      }
    },
    [dispatch, game?._id, buildSavableContentSnapshot],
  );

  const persistSettings = useCallback(async () => {
    if (!game?._id) return;
    const trimmedTitle = title.trim();
    try {
      await dispatch(
        saveGameSettings({
          game: game._id,
          title: trimmedTitle,
          description,
          program: trimmedTitle,
          category: "",
          settings: mergeGameSettings(game?.settings, {
            ...settingsFromProjectStatus(settings.projectStatus, game?.settings),
            contentType: "knowledge_base",
            completion_text: settings.completion_text,
            author_telegram: settings.author_telegram,
            allow_replay: settings.allow_replay,
            availableMessengers,
          }),
        }),
      ).unwrap();
      lastSavedSettingsRef.current = buildSettingsSnapshot();
    } catch {
      dispatch(addMessage({ type: "error", message: "Не удалось сохранить настройки" }));
    }
  }, [
    dispatch,
    game?._id,
    game?.settings,
    title,
    description,
    settings,
    availableMessengers,
    buildSettingsSnapshot,
  ]);

  useEffect(() => {
    const serialized = buildSavableContentSnapshot(topics, items);
    if (serialized === lastSavedContentRef.current || !game?._id) return;

    clearTimeout(contentSaveTimerRef.current);
    contentSaveTimerRef.current = setTimeout(() => {
      persistContent(topicsRef.current, itemsRef.current);
    }, 800);

    return () => clearTimeout(contentSaveTimerRef.current);
  }, [topics, items, game?._id, persistContent, buildSavableContentSnapshot]);

  useEffect(() => {
    const serialized = buildSettingsSnapshot();
    if (serialized === lastSavedSettingsRef.current || !game?._id) return;

    clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => {
      persistSettings();
    }, 800);

    return () => clearTimeout(settingsSaveTimerRef.current);
  }, [
    title,
    description,
    settings,
    availableMessengers,
    game?._id,
    buildSettingsSnapshot,
    persistSettings,
  ]);

  const updateActiveTopic = (patch) => {
    if (!activeTopicId) return;
    setTopics((prev) =>
      prev.map((topic) =>
        topic.id === activeTopicId ? { ...topic, ...patch } : topic,
      ),
    );
  };

  const updateActiveItem = (patch) => {
    if (!activeItemId) return;
    setItems((prev) =>
      prev.map((item) => (item.id === activeItemId ? { ...item, ...patch } : item)),
    );
  };

  const handleAddRootTopic = () => {
    const next = createEmptyTopic(null);
    setTopics((prev) => [...prev, next]);
    setActiveTopicId(next.id);
    setActiveItemId(null);
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set("type", "knowledge_base");
        params.set("topic", next.id);
        return params;
      },
      { replace: true },
    );
  };

  const handleAddSubtopic = () => {
    if (!activeTopicId) {
      handleAddRootTopic();
      return;
    }
    if (activeTopic && !isTopicComplete(activeTopic)) {
      dispatch(
        addMessage({
          type: "error",
          message: "Укажите название темы, прежде чем добавлять подтему",
        }),
      );
      return;
    }
    const next = createEmptyTopic(activeTopicId);
    setTopics((prev) => [...prev, next]);
    setActiveTopicId(next.id);
    setActiveItemId(null);
  };

  const handleDeleteTopic = (topicId) => {
    const topic = topics.find((entry) => entry.id === topicId);
    const isRootTopic = topic && (topic.parentId ?? null) === null;
    const idsToRemove = collectTopicDescendantIds(topics, topicId);
    const nextTopics = topics.filter((entry) => !idsToRemove.has(entry.id));
    setTopics(nextTopics);
    setItems((prev) => prev.filter((item) => !idsToRemove.has(item.topicId)));

    if (idsToRemove.has(activeTopicId)) {
      setActiveItemId(null);
      if (isRootTopic && topicId === scopedRootId) {
        const remainingRoots = getRootTopics(nextTopics);
        if (remainingRoots[0]) {
          setActiveTopicId(remainingRoots[0].id);
          setSearchParams(
            (current) => {
              const params = new URLSearchParams(current);
              params.set("type", "knowledge_base");
              params.set("topic", remainingRoots[0].id);
              return params;
            },
            { replace: true },
          );
        } else {
          setActiveTopicId(null);
          setSearchParams(
            (current) => {
              const params = new URLSearchParams(current);
              params.delete("topic");
              return params;
            },
            { replace: true },
          );
        }
      } else {
        setActiveTopicId(null);
      }
    }

    dispatch(
      addMessage({
        type: "info",
        message: isRootTopic ? "Тема удалена" : "Подтема удалена",
      }),
    );
  };

  const handleAddItem = async () => {
    if (!activeTopicId || !activeTopicIsLeaf) {
      dispatch(
        addMessage({
          type: "error",
          message: "Выберите конечную подтему без вложенных разделов",
        }),
      );
      return;
    }

    if (activeItem && !isItemComplete(activeItem)) {
      dispatch(
        addMessage({
          type: "error",
          message: "Заполните вопрос и ответ, прежде чем добавлять новый",
        }),
      );
      return;
    }

    clearTimeout(contentSaveTimerRef.current);
    await persistContent(topics, items);
    const next = createEmptyItem(activeTopicId);
    setItems((prev) => [...prev, next]);
    setActiveItemId(next.id);
  };

  const handleViewChange = async (nextView) => {
    if (view === "settings" && nextView !== "settings") {
      clearTimeout(settingsSaveTimerRef.current);
      await persistSettings();
    }
    setView(nextView);
  };

  const handleDeleteItem = (itemId) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
    if (activeItemId === itemId) {
      setActiveItemId(null);
    }
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeItem || !user?.id || !game?._id) return;

    dispatch(
      loadKbVideo({
        userId: user.id,
        gameId: game._id,
        itemId: activeItem.id,
        file,
      }),
    ).then((result) => {
      if (result.payload?.kbItems) {
        setItems((prev) => {
          const merged = prev.map((item) => {
            const saved = result.payload.kbItems.find((entry) => entry.id === item.id);
            return saved ?? item;
          });
          lastSavedContentRef.current = buildSavableContentSnapshot(topics, merged);
          return merged;
        });
      }
    });
  };

  const handlePoster = (event, gameId) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      dispatch(
        loadPoster({
          gameId,
          base64String: reader.result,
          onSucces: () => dispatch(getGame({ gameId })),
        }),
      );
    };
  };

  const renderProjectSettings = () => (
    <section className="trainer-editor__card trainer-editor__card--settings">
      <h3>Настройки проекта</h3>
      <TextField
        label="Название в боковой панели"
        size="small"
        fullWidth
        sx={fieldSx}
        value={title}
        onChange={(e) => setTitle(sanitizePlainText(e.target.value, 120))}
        placeholder="Например: База знаний компании"
      />
      <TextField
        label="Приветственный текст"
        size="small"
        fullWidth
        multiline
        minRows={3}
        sx={fieldSx}
        value={description}
        onChange={(e) =>
          setDescription(sanitizePlainText(e.target.value, 2000))
        }
      />
      <div className="trainer-editor__settings-row">
        <TextField
          select
          label="Статус"
          size="small"
          sx={{ ...fieldSx, flex: 1 }}
          value={settings.projectStatus}
          onChange={(e) =>
            setSettings((prev) => ({
              ...prev,
              projectStatus: e.target.value,
            }))
          }
        >
          <MenuItem value="draft">Черновик</MenuItem>
          <MenuItem value="published">Опубликован</MenuItem>
          <MenuItem value="hidden">Скрыт</MenuItem>
        </TextField>
        <TextField
          label="Telegram HR"
          size="small"
          sx={{ ...fieldSx, flex: 1.4 }}
          value={settings.author_telegram}
          onChange={(e) =>
            setSettings((prev) => ({
              ...prev,
              author_telegram: sanitizePlainText(e.target.value, 80),
            }))
          }
        />
      </div>
      <div className="kb-editor__poster">
        <InputFileUpload
          label="Обложка"
          accept="image/*"
          onChange={(event) => handlePoster(event, game._id)}
        />
      </div>
    </section>
  );

  return (
    <div className="trainer-editor kb-editor">
      <div className="trainer-editor__toolbar trainer-editor__toolbar--tabs">
        <Button
          variant="outlined"
          size="small"
          className={`app-editor__tab${view === "editor" ? " app-editor__tab--active" : ""}`}
          startIcon={<EditOutlinedIcon />}
          onClick={() => handleViewChange("editor")}
        >
          Редактор
        </Button>
        <Button
          variant="outlined"
          size="small"
          className={`app-editor__tab${view === "settings" ? " app-editor__tab--active" : ""}`}
          startIcon={<SettingsOutlinedIcon />}
          onClick={() => handleViewChange("settings")}
        >
          Настройки
        </Button>
      </div>

      <div className="trainer-editor__meta-bar">
        <span className="trainer-editor__meta-theme">
          {title.trim() || "Без названия"}
          {topicPath.length ? (
            <>
              {" "}
              /{" "}
              <strong>
                {topicPath.map((topic) => topic.title?.trim() || "Без названия").join(" / ")}
              </strong>
            </>
          ) : null}
        </span>
        {view === "editor" ? (
          <span className="trainer-editor__meta-count">
            {formatQuestionCount(items.filter(isItemComplete).length)}
          </span>
        ) : null}
      </div>

      {view === "settings" ? (
        <div className="app-editor-settings kb-editor__settings">
          {renderProjectSettings()}
          <ProjectTeamAccess
            gameId={game._id}
            value={availableMessengers}
            onChange={setAvailableMessengers}
          />
          <ProjectDeleteSection label="База знаний будет удалена" />
        </div>
      ) : (
        <div className="trainer-editor__workspace kb-editor__workspace">
          <section className="trainer-editor__card trainer-editor__card--questions">
            <div className="trainer-editor__card-head">
              <div>
                <h3>Темы и вопросы</h3>
                <p className="trainer-editor__card-lead">
                  Создайте темы и подтемы. Вопросы добавляйте только в конечные
                  разделы без вложенных подтем.
                </p>
              </div>
              <div className="kb-editor__head-actions">
                <Button
                  size="small"
                  variant="outlined"
                  className="app-btn app-btn--secondary app-editor__add-question-btn"
                  startIcon={<AddIcon />}
                  onClick={handleAddRootTopic}
                >
                  Тема
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  className="app-btn app-btn--secondary app-editor__add-question-btn"
                  startIcon={<AddIcon />}
                  onClick={handleAddSubtopic}
                  disabled={!activeTopicId}
                >
                  Подтема
                </Button>
              </div>
            </div>

            <div className="kb-editor__layout">
              <div className="kb-editor__topics-panel">
                {!topics.length ? (
                  <p className="trainer-editor__empty kb-editor__empty">
                    Добавьте первую тему, например «Продажи».
                  </p>
                ) : (
                  <TopicTreeNodes
                    topics={topics}
                    roots={visibleTopicRoots}
                    depth={0}
                    activeTopicId={activeTopicId}
                    onSelect={handleSelectTopic}
                    onDelete={handleDeleteTopic}
                  />
                )}
              </div>

              <div className="kb-editor__content-panel">
                {activeTopic ? (
                  <>
                    <TextField
                      label="Название темы"
                      size="small"
                      fullWidth
                      sx={fieldSx}
                      value={activeTopic.title}
                      onChange={(e) =>
                        updateActiveTopic({
                          title: sanitizePlainText(e.target.value, 120),
                        })
                      }
                      placeholder="Например: Переговоры"
                    />

                    {!activeTopicIsLeaf ? (
                      <p className="kb-editor__leaf-hint">
                        В этой теме есть подтемы. Выберите конечный раздел или
                        добавьте подтему, чтобы создавать вопросы.
                      </p>
                    ) : (
                      <div className="trainer-editor__questions-layout">
                        <div className="trainer-editor__questions-nav">
                          {topicItems.length === 0 ? (
                            <p className="trainer-editor__empty kb-editor__empty">
                              В этой подтеме пока нет вопросов.
                            </p>
                          ) : (
                            <ul className="trainer-editor__question-list kb-editor__list">
                              {topicItems.map((item, index) => (
                                <li
                                  key={item.id}
                                  className={`trainer-editor__question-item kb-editor__list-item${
                                    activeItemId === item.id
                                      ? " trainer-editor__question-item--active kb-editor__list-item--active"
                                      : ""
                                  }`}
                                >
                                  <button
                                    type="button"
                                    className="trainer-editor__question-btn kb-editor__list-btn"
                                    onClick={() => setActiveItemId(item.id)}
                                  >
                                    <span className="trainer-editor__question-num">
                                      {index + 1}
                                    </span>
                                    <MenuBookOutlinedIcon fontSize="small" />
                                    {item.question || "Без названия"}
                                  </button>
                                  <IconButton
                                    size="small"
                                    className="kb-editor__delete-btn"
                                    aria-label="Удалить"
                                    onClick={() => handleDeleteItem(item.id)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        <div className="trainer-editor__questions-editor">
                          {activeItem ? (
                            <div className="trainer-editor__question-form">
                              <TextField
                                label="Вопрос сотрудника"
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                sx={fieldSx}
                                value={activeItem.question}
                                onChange={(e) =>
                                  updateActiveItem({
                                    question: sanitizePlainText(e.target.value, 500),
                                  })
                                }
                                placeholder="Например: Как оформить отпуск?"
                              />

                              <span className="trainer-editor__answers-label">
                                Формат ответа
                              </span>
                              <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={activeItem.answerMode}
                                onChange={(_, value) => {
                                  if (value) updateActiveItem({ answerMode: value });
                                }}
                                className="app-dialog__type-toggle kb-editor__answer-mode"
                              >
                                <ToggleButton value="text">
                                  <TextFieldsOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                                  Текст
                                </ToggleButton>
                                <ToggleButton value="video">
                                  <VideocamOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                                  Видео
                                </ToggleButton>
                              </ToggleButtonGroup>

                              {activeItem.answerMode === "text" ? (
                                <TextField
                                  label="Ответ"
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={5}
                                  sx={fieldSx}
                                  value={activeItem.answerText}
                                  onChange={(e) =>
                                    updateActiveItem({
                                      answerText: sanitizePlainText(e.target.value, 4000),
                                    })
                                  }
                                  placeholder="Текст ответа, который увидит сотрудник в боте"
                                />
                              ) : (
                                <div className="kb-editor__video-upload">
                                  <InputFileUpload
                                    label="Видео-ответ"
                                    accept="video/*"
                                    onChange={handleVideoUpload}
                                  />
                                  {activeItem.hasVideo ? (
                                    <p className="kb-editor__video-status">Видео загружено</p>
                                  ) : null}
                                </div>
                              )}

                              <Button
                                size="small"
                                variant="contained"
                                className="app-btn app-btn--primary app-editor__add-question-btn kb-editor__add-submit"
                                startIcon={<AddIcon />}
                                onClick={handleAddItem}
                              >
                                Добавить
                              </Button>
                            </div>
                          ) : (
                            <div className="trainer-editor__questions-placeholder kb-editor__questions-placeholder">
                              <p>Выберите вопрос из списка или создайте новый.</p>
                              <Button
                                size="small"
                                variant="contained"
                                className="app-btn app-btn--primary app-editor__add-question-btn"
                                startIcon={<AddIcon />}
                                onClick={handleAddItem}
                              >
                                Добавить вопрос
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="trainer-editor__questions-placeholder">
                    Выберите тему слева или создайте новую.
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
